/* Reactive hook for feed update progress.
   Queries feedConfig for real-time isUpdating / currentIndex / totalSources
   and derives a 0-1 progress value plus a UI-friendly state. */

import { useQuery, useAction, useMutation } from "convex/react";
import { useCallback, useRef, useState, useEffect } from "react";
import { api } from "../../convex/_generated/api";

export type FeedUpdateState = "idle" | "updating" | "done";

export interface FeedUpdateProgress {
  state: FeedUpdateState;
  /** 0–1 overall progress */
  progress: number;
  currentIndex: number;
  totalSources: number;
  /** Epoch ms of the last completed update, or undefined if never updated */
  lastUpdatedAt: number | undefined;
  /** Trigger a manual feed update */
  startUpdate: () => void;
  /** Cancel an in-progress update */
  cancelUpdate: () => void;
}

const DONE_DISPLAY_MS = 2500;

export function useFeedUpdateProgress(): FeedUpdateProgress {
  const feedConfig = useQuery(api.feedConfig.get, {});
  const updateAllFeeds = useAction(api.updateFeeds.updateAllFeeds);
  const cancelUpdateMutation = useMutation(api.feedConfig.cancelUpdate);

  const [showDone, setShowDone] = useState(false);
  const wasUpdatingRef = useRef(false);
  const doneTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const isUpdating = feedConfig?.updateProgress?.isUpdating ?? false;
  const currentIndex = feedConfig?.updateProgress?.currentIndex ?? 0;
  const totalSources = feedConfig?.updateProgress?.totalSources ?? 0;

  // Detect transition from updating → not updating to show "done" briefly
  useEffect(() => {
    if (isUpdating) {
      wasUpdatingRef.current = true;
      setShowDone(false);
      if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
    } else if (wasUpdatingRef.current) {
      wasUpdatingRef.current = false;
      setShowDone(true);
      doneTimerRef.current = setTimeout(() => setShowDone(false), DONE_DISPLAY_MS);
    }
    return () => {
      if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
    };
  }, [isUpdating]);

  const progress =
    totalSources > 0 ? Math.min(currentIndex / totalSources, 1) : 0;

  const state: FeedUpdateState = isUpdating
    ? "updating"
    : showDone
      ? "done"
      : "idle";

  const startUpdate = useCallback(() => {
    updateAllFeeds({ triggerType: "manual" });
  }, [updateAllFeeds]);

  const cancelUpdate = useCallback(() => {
    cancelUpdateMutation();
  }, [cancelUpdateMutation]);

  return {
    state,
    progress,
    currentIndex,
    totalSources,
    lastUpdatedAt: feedConfig?.lastUpdated,
    startUpdate,
    cancelUpdate,
  };
}
